import React, { useMemo, useCallback, memo, useState, useEffect } from 'react';
import { Flex, Divider, Dropdown } from 'antd';
import { CustomShipmentText } from '@/components';
import {
  ArrowLeft,
  ArrowRight,
  BatteryIcon,
  ChevronDownIcon,
  HumidityIcon,
  LightIcon,
  PressureIcon,
  RightTailArrowIcon,
  ShockIcon,
  TempratureRangeIcon,
  TiltIcon,
} from '../../../assets/icons/icons';
import { SingleShipmentDataExtended } from '@/apis/types';
import { CoCEventType } from '@/common/enum';
import { TrackingPoint } from '../ShipmentMap';
import { PointState } from './types';
import styles from './MapPointsCards.module.css';
import { shareHidesSensorPoints } from '../partials/sensorVitals';
import { getFormattedTime } from '@/common/utils/getFormattedTimeUtils';
import { parseShipmentDayjs } from '@/common/utils/utils';
import dayjs from 'dayjs';
import { useToken } from '@/common/helper';
import {
  getIndexedPrimarySensorPoints,
  getSensorPointId,
  hasValidSensorCoordinates,
  isSensorPointId,
  type SensorTrackShipment,
} from '../mapSensorUtils';

interface MapEvent extends Partial<TrackingPoint> {
  id: string;
  type: 'sensor' | 'courier';
  latitude: number;
  longitude: number;
  lat_updated?: string;
  action?: string;
  location?: string;
  timestamp?: string;
  handler?: string;
}

/** Mirrors detail API `sensors` items; `id` may be absent on partial views. */
interface SensorsData {
  id?: string;
  sensor_id?: string;
  sensor_tracking_id?: string;
  display_label?: string;
  priority?: number;
}

interface MapPointsCardsProps {
  pointState: PointState;
  setPointState: React.Dispatch<React.SetStateAction<PointState>>;
  setShowPointCard: React.Dispatch<React.SetStateAction<boolean>>;
  shipment: Partial<SingleShipmentDataExtended>;
  currentPointSelected: number;
  setCurrentPointSelected: React.Dispatch<React.SetStateAction<number>>;
  selectedPointType: string | null;
  setSelectedPointType: React.Dispatch<React.SetStateAction<string | null>>;
  selectedEventId?: string | null;
  setSelectedEventId?: React.Dispatch<React.SetStateAction<string | null>>;
  /** Recenters the map on a point chosen via Previous/Next; omitted when there is no map. */
  onRecenter?: (coordinates: { lat: number; lng: number }) => void;
}
const SensorDataRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  unit?: string;
  precision?: number;
  ageSeconds?: number | null;
  measuredAt?: string | null;
}> = ({ icon, label, value, unit = '', precision, ageSeconds, measuredAt }) => {
  const [revealedMeasurement, setRevealedMeasurement] = useState<string | null>(
    null
  );
  // `0` is a real reading (0 C, 0 G), so only absent values are hidden.
  if (value === null || value === undefined || value === '') return null;

  const numericValue = precision != null ? Number(value) : NaN;
  const displayValue =
    precision != null && Number.isFinite(numericValue)
      ? numericValue.toFixed(precision)
      : value;

  // Profile-local display timestamps lose their UTC offset at DST changes.
  // The backend derives this elapsed time from the original UTC observations.
  const ageMinutes =
    ageSeconds != null && Number.isFinite(ageSeconds) && ageSeconds > 0
      ? Math.max(1, Math.round(ageSeconds / 60))
      : 0;
  const ageLabel = ageMinutes > 0 ? `${ageMinutes} min earlier` : null;
  // The age is rounded; display the actual source timestamp instead of subtracting
  // the label from GPS time. API timestamps are already in the user's timezone.
  const measuredDateTime = measuredAt
    ? parseShipmentDayjs(measuredAt)?.format('MMM DD, YYYY h:mm A')
    : null;
  // A carried reading can appear at multiple GPS points with different ages.
  const measurementKey = `${measuredAt}:${ageSeconds}`;
  const showMeasuredTime = revealedMeasurement === measurementKey;

  return (
    <Flex justify='space-between'>
      <Flex gap={6} align='center'>
        {icon}
        <CustomShipmentText text={label} fontWeight={600} />
        {ageLabel && measuredDateTime ? (
          <button
            type='button'
            aria-label={
              showMeasuredTime
                ? `Show ${label.toLowerCase()} age`
                : `Show ${label.toLowerCase()} measurement date and time`
            }
            aria-pressed={showMeasuredTime}
            onClick={() =>
              setRevealedMeasurement(showMeasuredTime ? null : measurementKey)
            }
            className={styles.ageToggle}
          >
            {showMeasuredTime ? measuredDateTime : ageLabel}
          </button>
        ) : ageLabel ? (
          <CustomShipmentText text={ageLabel} color='var(--text-light)' />
        ) : null}
      </Flex>
      <CustomShipmentText
        text={`${displayValue} ${unit}`}
        color='var(--text-light)'
      />
    </Flex>
  );
};

const MapPointsCards: React.FC<MapPointsCardsProps> = ({
  pointState,
  setPointState,
  setShowPointCard,
  shipment,
  currentPointSelected,
  setCurrentPointSelected,
  selectedPointType,
  setSelectedPointType,
  selectedEventId,
  setSelectedEventId,
  onRecenter,
}) => {
  const { decodedUserToken } = useToken();

  const isTimeZone = decodedUserToken()?.time_zone_enable;
  const { relative, full } = useMemo(
    () => getFormattedTime(pointState.lat_updated ?? '', isTimeZone),
    [pointState.lat_updated, isTimeZone]
  );

  const allEvents = useMemo<MapEvent[]>(() => {
    // Process sensor events

    // The detail payload's monitor points are TrackingPoints whose vitals may be null.
    // A share link with sensor data off has no sensor points to step through.
    const primarySensorPoints = shareHidesSensorPoints(shipment)
      ? []
      : getIndexedPrimarySensorPoints(
          shipment as SensorTrackShipment<TrackingPoint>
        );
    const sensorEvents: MapEvent[] = primarySensorPoints
      .filter(hasValidSensorCoordinates)
      .map((point) => ({
        id: getSensorPointId(point),
        type: 'sensor' as const,
        latitude: point.latitude as number,
        longitude: point.longitude as number,
        lat_updated: point.lat_updated,
        // `??` rather than `||`: a reading of 0 is real and must survive.
        temperature: point.temperature ?? '',
        temperature_updated: point.temperature_updated ?? null,
        temperature_age_seconds: point.temperature_age_seconds ?? null,
        battery: point.battery ?? '',
        battery_updated: point.battery_updated ?? null,
        battery_age_seconds: point.battery_age_seconds ?? null,
        humidity: point.humidity ?? '',
        pressure: point.pressure ?? '',
        light: point.light ?? '',
        shock_value: point.shock_value ?? '',
        tilt: point.tilt ?? '',
      }));
    // Process courier events
    const courierEvents: MapEvent[] = (
      shipment?.chain_of_custody || []
    ).flatMap((item) => {
      const { latitude, longitude } = item;
      // Truthiness also drops 0, which marks a scan without a location.
      if (item.type !== CoCEventType.CustodyEvent || !latitude || !longitude) {
        return [];
      }
      return [
        {
          id: item.id,
          type: 'courier' as const,
          latitude,
          longitude,
          lat_updated: item.timestamp,
          action: item.action,
          location: item.location,
          handler: item.handler,
        },
      ];
    });

    // Combine and sort events by timestamp
    return [...sensorEvents, ...courierEvents].sort((a, b) => {
      const timeA = a.lat_updated
        ? dayjs(a.lat_updated).valueOf()
        : Number.MAX_SAFE_INTEGER;
      const timeB = b.lat_updated
        ? dayjs(b.lat_updated).valueOf()
        : Number.MAX_SAFE_INTEGER;
      return timeA - timeB;
    });
  }, [shipment]);

  const menuItems = useMemo(() => {
    if (!Array.isArray(shipment?.sensors)) {
      return [];
    }

    const sortedApples = [...shipment.sensors].sort(
      (a: SensorsData, b: SensorsData) => (a.priority ?? 0) - (b.priority ?? 0)
    );

    const mappedItems = sortedApples.map(
      (item: SensorsData, index: number) => ({
        key:
          item.id ??
          `${item.sensor_id ?? ''}-${item.sensor_tracking_id ?? ''}-${item.priority ?? index}`,
        label: item.display_label ?? '',
        style: { fontSize: '12px', color: 'var(--text-secondary)' },
      })
    );

    return [
      {
        key: 'all',
        label: 'All sensors',
        style: { fontSize: '12px', color: 'var(--text-secondary)' },
      },
      ...mappedItems,
    ];
  }, [shipment]);

  const menu = {
    items: menuItems,
    onClick: ({ key }: { key: string }) => {
      setSelectedKey(key);
    },
  };
  const [selectedKey, setSelectedKey] = useState(menuItems[0]?.key);

  // Handle point selection
  const handlePointSelect = useCallback(
    (index: number) => {
      const point = allEvents[index];
      if (!point) return;
      setSelectedEventId?.(point.id);

      setCurrentPointSelected(index);
      setSelectedPointType(point.type);
      // Events without usable coordinates leave the map where it is.
      if (
        hasValidSensorCoordinates({
          latitude: point.latitude,
          longitude: point.longitude,
        })
      ) {
        onRecenter?.({ lat: point.latitude, lng: point.longitude });
      }
      setPointState((prev) => ({
        ...prev,
        temperature: point.temperature ?? '',
        temperature_updated: point.temperature_updated ?? null,
        temperature_age_seconds: point.temperature_age_seconds ?? null,
        battery: point.battery ?? '',
        battery_updated: point.battery_updated ?? null,
        battery_age_seconds: point.battery_age_seconds ?? null,
        light: point.light ?? '',
        humidity: point.humidity ?? '',
        tilt: point.tilt ?? '',
        pressure: point.pressure ?? '',
        shock_value: point.shock_value != null ? String(point.shock_value) : '',
        cell_signal: point.cell_signal ?? '',
        lat_updated: point.lat_updated ?? '',
        action: point.action ?? '',
        location: point.location ?? '',
        timestamp: point.timestamp ?? '',
        handler: point.handler ?? '',
      }));
    },
    [
      allEvents,
      setCurrentPointSelected,
      setSelectedPointType,
      setPointState,
      setSelectedEventId,
      onRecenter,
    ]
  );

  const selectedEventIndex = selectedEventId
    ? allEvents.findIndex((event) => event.id === selectedEventId)
    : -1;

  // A minimal monitor_info snapshot is replaced by shipment_monitor history
  // after the detail request finishes. If that snapshot is absent from the
  // history response, close it instead of silently reusing its old array index.
  useEffect(() => {
    if (
      selectedEventId &&
      isSensorPointId(selectedEventId) &&
      selectedEventIndex < 0
    ) {
      setSelectedEventId?.(null);
      setShowPointCard(false);
    }
  }, [
    selectedEventId,
    selectedEventIndex,
    setSelectedEventId,
    setShowPointCard,
  ]);

  const navigationIndex =
    selectedEventIndex >= 0 ? selectedEventIndex : currentPointSelected;

  // Navigation handlers
  const handlePrev = useCallback(() => {
    if (navigationIndex > 0) {
      handlePointSelect(navigationIndex - 1);
    }
  }, [navigationIndex, handlePointSelect]);

  const handleNext = useCallback(() => {
    if (navigationIndex < allEvents.length - 1) {
      handlePointSelect(navigationIndex + 1);
    }
  }, [navigationIndex, allEvents.length, handlePointSelect]);

  // Memoize point type title
  const pointTypeTitle = useMemo(() => {
    if (selectedPointType === 'sensor') return 'Sensor reading';
    if (selectedPointType === 'courier') return 'Provider scan';
    return '';
  }, [selectedPointType]);

  const isPrevDisabled = navigationIndex <= 0;
  const isNextDisabled = navigationIndex >= allEvents.length - 1;
  const selectedLabel =
    menuItems.find((item) => item.key === selectedKey)?.label || 'Select';
  const hasMeasurement = [
    pointState.temperature,
    pointState.battery,
    pointState.shock_value,
    pointState.light,
    pointState.tilt,
    pointState.pressure,
    pointState.humidity,
  ].some((value) => value !== null && value !== undefined && value !== '');

  return (
    <div className='custom-card-drawer custom-map-card overide-custom-drawer'>
      <div className={`custom-card-drawer-content ${styles.content}`}>
        <Flex justify='space-between' align='center'>
          <span
            className={`c-pointer ${styles.closeButton}`}
            onClick={() => setShowPointCard(false)}
            role='button'
            aria-label='Close card'
          >
            <RightTailArrowIcon />
          </span>
          <Flex
            gap={8}
            align='center'
            className='custom-card-drawer-header-actions'
          >
            <button
              type='button'
              className={styles.navButton}
              onClick={handlePrev}
              disabled={isPrevDisabled}
              aria-label='Previous point'
            >
              <ArrowLeft
                fill={isPrevDisabled ? '#CCCCCC' : 'var(--text-primary)'}
              />
            </button>

            <button
              type='button'
              title='Next'
              className={styles.navButton}
              onClick={handleNext}
              disabled={isNextDisabled}
              aria-label='Next point'
            >
              <ArrowRight
                fill={isNextDisabled ? '#CCCCCC' : 'var(--text-primary)'}
              />
            </button>
          </Flex>
        </Flex>

        <Flex vertical gap={8}>
          <Flex align='center' gap={8}>
            <CustomShipmentText
              text={pointTypeTitle}
              fontWeight={600}
              fontSize='16px'
              lineHeight='20px'
            />
            {selectedPointType === 'sensor' &&
              (shipment?.sensors?.length ?? 0) > 0 && (
                <Dropdown menu={menu}>
                  <Flex align='center' gap={4}>
                    <CustomShipmentText
                      text={selectedLabel}
                      color='var(--text-secondary)'
                      fontWeight={600}
                    />
                    <ChevronDownIcon size={16} color='var(--text-secondary)' />
                  </Flex>
                </Dropdown>
              )}
          </Flex>
          <CustomShipmentText
            text={`${full}${relative ? ` • ${relative}` : ''}  ${shipment.sensor?.type && selectedPointType === 'sensor' ? ` • ${shipment.sensor?.type}` : ''}`}
            color='var(--text-secondary)'
          />
          <Divider
            className='notification-divider'
            style={{ margin: '8px 0' }}
          />
          {selectedPointType === 'sensor' ? (
            <Flex vertical gap={8}>
              <SensorDataRow
                icon={<TempratureRangeIcon />}
                label='Temperature'
                value={pointState.temperature ?? ''}
                unit='°C'
                ageSeconds={pointState.temperature_age_seconds}
                measuredAt={pointState.temperature_updated}
              />
              <SensorDataRow
                icon={<ShockIcon />}
                label='Shock'
                value={pointState.shock_value ?? ''}
                unit='G'
                precision={2}
              />

              <SensorDataRow
                icon={<LightIcon />}
                label='Light'
                value={pointState.light ?? ''}
                unit='lux'
              />
              <SensorDataRow
                icon={<TiltIcon />}
                label='Tilt'
                value={pointState.tilt ?? ''}
                unit='deg'
              />
              <SensorDataRow
                icon={<PressureIcon />}
                label='Pressure'
                value={pointState.pressure ?? ''}
                unit='PSI'
              />
              <SensorDataRow
                icon={<HumidityIcon />}
                label='Humidity'
                value={pointState.humidity ?? ''}
                unit='%'
              />
              <SensorDataRow
                icon={<BatteryIcon />}
                label='Battery'
                value={pointState.battery ?? ''}
                unit='%'
                ageSeconds={pointState.battery_age_seconds}
                measuredAt={pointState.battery_updated}
              />
              {!hasMeasurement && (
                <CustomShipmentText
                  text='No measurements available for this point'
                  color='var(--text-light)'
                />
              )}
            </Flex>
          ) : (
            <Flex
              vertical
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--light-gray)',
                borderRadius: '8px',
              }}
            >
              <CustomShipmentText
                text={pointState.action}
                fontSize='14px'
                fontWeight={600}
              />
              <CustomShipmentText text={pointState.location} fontSize='10px' />
            </Flex>
          )}
        </Flex>
      </div>
    </div>
  );
};

export default memo(MapPointsCards);
